export interface MicrophoneIntentScope {
  generation: number;
  session: unknown;
}

export type MicrophoneController = "always-on" | "ptt" | null;

interface MicrophoneIntent extends MicrophoneIntentScope {
  active: boolean;
  controller: MicrophoneController;
  requestId: number;
}

export class SessionMicrophoneIntentController {
  #currentScope: MicrophoneIntentScope | null = null;
  #controller: MicrophoneController = null;
  #desiredActive = false;
  #queue: Promise<void> = Promise.resolve();
  #requestId = 0;

  beginSession(scope: MicrophoneIntentScope): void {
    if (
      this.#currentScope?.generation === scope.generation &&
      this.#currentScope.session === scope.session
    ) {
      return;
    }
    this.#currentScope = scope;
    this.#controller = null;
    this.#desiredActive = false;
    this.#requestId += 1;
  }

  invalidate(): void {
    this.#currentScope = null;
    this.#controller = null;
    this.#desiredActive = false;
    this.#requestId += 1;
  }

  get controller(): MicrophoneController {
    return this.#controller;
  }

  request(
    scope: MicrophoneIntentScope,
    active: boolean,
    controller: MicrophoneController = active ? this.#controller : null
  ): MicrophoneIntent {
    this.beginSession(scope);
    this.#desiredActive = active;
    this.#controller = active ? controller : null;
    return {
      ...scope,
      active,
      controller: this.#controller,
      requestId: ++this.#requestId,
    };
  }

  capture(scope: MicrophoneIntentScope): MicrophoneIntent | null {
    this.beginSession(scope);
    if (!this.#desiredActive) {
      return null;
    }
    return this.snapshot(scope);
  }

  snapshot(scope: MicrophoneIntentScope): MicrophoneIntent {
    this.beginSession(scope);
    return {
      ...scope,
      active: this.#desiredActive,
      controller: this.#controller,
      requestId: this.#requestId,
    };
  }

  isCurrent(intent: MicrophoneIntent): boolean {
    return (
      this.#currentScope?.generation === intent.generation &&
      this.#currentScope.session === intent.session &&
      this.#requestId === intent.requestId &&
      this.#desiredActive === intent.active &&
      this.#controller === intent.controller
    );
  }

  async enqueue(
    intent: MicrophoneIntent,
    operation: () => Promise<void>
  ): Promise<"applied" | "cancelled"> {
    let result: "applied" | "cancelled" = "cancelled";
    const queued = (async () => {
      await Promise.allSettled([this.#queue]);
      if (!this.isCurrent(intent)) {
        return;
      }
      await operation();
      if (this.isCurrent(intent)) {
        result = "applied";
      }
    })();
    this.#queue = (async () => {
      await Promise.allSettled([queued]);
    })();
    await queued;
    return result;
  }
}
